import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';
import apiClient from '@/util/apiClient';

/**
 * Group Request Store
 *
 * Handles loading and caching group request records from:
 * - GET    /api/v1/groups/request/all
 * - GET    /api/v1/groups/request/{request_number}
 * - GET    /api/v1/groups/request/group/{group_number}
 * - POST   /api/v1/groups/request/save
 * - DELETE /api/v1/groups/request/delete/{request_number}
 *
 * State is partitioned by:
 * - `allRequests` => full cached list from `/all`
 * - `requestsByGroup[groupNumber]` => cached request list for a group
 * - `requestByNumber[requestNumber]` => cached single request lookup
 * - loading flags for UI coordination
 */
export const useGroupRequestStore = defineStore('groupRequestStore', {
	state: () => ({
		/**
		 * @type {Object.<number, Array<any>>}
		 */
		requestsByGroup: {},
		/**
		 * @type {Object.<number, any|null>}
		 */
		requestByNumber: {},
		/**
		 * @type {boolean}
		 */
		loadingByGroup: {},
		/**
		 * @type {Object.<number, boolean>}
		 */
		loadingByNumber: {},
		/**
		 * @type {boolean}
		 */
		saving: false,
		/**
		 * @type {boolean}
		 */
		deleting: false,
		/**
		 * @type {Object.<number, boolean>}
		 */
		loadedByGroup: {},
		/**
		 * @type {Object.<number, boolean>}
		 */
		loadedByNumber: {}
	}),
	getters: {
		/**
		 * Returns cached requests for a group.
		 *
		 * @param {ReturnType<typeof useGroupRequestStore>['$state']} s
		 * @returns {(groupNumber: number|string) => Array<any>}
		 */
		getRequestsByGroup: (s) => (groupNumber) => s.requestsByGroup[groupNumber] ?? [],

		/**
		 * Returns a cached request by request number.
		 *
		 * @param {ReturnType<typeof useGroupRequestStore>['$state']} s
		 * @returns {(requestNumber: number|string) => any|null}
		 */
		getRequestByNumber: (s) => (requestNumber) => s.requestByNumber[requestNumber] ?? null,

		/**
		 * Whether a group request list has been loaded.
		 *
		 * @param {ReturnType<typeof useGroupRequestStore>['$state']} s
		 * @returns {(groupNumber: number|string) => boolean}
		 */
		isGroupLoaded: (s) => (groupNumber) => !!s.loadedByGroup[groupNumber],

		/**
		 * Whether a request-number lookup has been loaded.
		 *
		 * @param {ReturnType<typeof useGroupRequestStore>['$state']} s
		 * @returns {(requestNumber: number|string) => boolean}
		 */
		isRequestLoaded: (s) => (requestNumber) => !!s.loadedByNumber[requestNumber]
	},
	actions: {
		/**
		 * Robustly extracts an array payload from common backend response shapes.
		 *
		 * @param {any} raw
		 * @returns {Array<any>}
		 */
		extractList(raw) {
			return Array.isArray(raw) ? raw : Array.isArray(raw?.requests) ? raw.requests : Array.isArray(raw?.data) ? raw.data : [];
		},

		/**
		 * Robustly extracts a single payload from common backend response shapes.
		 *
		 * Supports Optional-like backend responses as either:
		 * - the DTO directly
		 * - null / undefined
		 * - { data: DTO }
		 * - { request: DTO }
		 *
		 * @param {any} raw
		 * @returns {any|null}
		 */
		extractSingle(raw) {
			if (raw == null) return null;
			if (Array.isArray(raw)) return raw[0] ?? null;
			if (raw?.data !== undefined && !Array.isArray(raw.data)) return raw.data;
			if (raw?.request !== undefined) return raw.request;
			return raw;
		},

		/**
		 * Loads and caches a single group request by request number.
		 *
		 * ROUTE: GET /groups/request/{request_number}
		 *
		 * @param {number|string} requestNumber
		 * @param {{mode?: string}} [opts={}]
		 * @returns {Promise<any|null>}
		 */
		async getRequestByRequestNumber(requestNumber, opts = {}) {
			if (shouldThrottle('getRequestByRequestNumber', 100)) {
				posthog.capture('throttled_action', { action: 'getRequestByRequestNumber' });
				return [];
			}
			const key = Number(requestNumber);
			if (!Number.isFinite(key)) return null;

			if (opts.mode !== 'refresh' && this.loadedByNumber[key]) {
				return this.requestByNumber[key] ?? null;
			}

			this.loadingByNumber[key] = true;

			try {
				const { data } = await apiClient.get(`/groups/request/${key}`);
				const request = this.extractSingle(data);

				this.requestByNumber[key] = request;
				this.loadedByNumber[key] = true;

				const groupNumber = Number(request?.group?.groupNumber ?? request?.groupNumber ?? request?.group_number);
				if (request && Number.isFinite(groupNumber)) {
					const current = this.requestsByGroup[groupNumber] ?? [];
					const filtered = current.filter((r) => Number(r?.requestNumber ?? r?.request_number) !== key);
					this.requestsByGroup[groupNumber] = [...filtered, request];
				}

				return request;
			} catch {
				this.requestByNumber[key] = null;
				this.loadedByNumber[key] = true;
				return null;
			} finally {
				this.loadingByNumber[key] = false;
			}
		},

		/**
		 * Loads and caches all requests for a group.
		 *
		 * ROUTE: GET /groups/request/group/{group_number}
		 *
		 * @param {number|string} groupNumber
		 * @param {{mode?: string}} [opts={}]
		 * @returns {Promise<Array<any>>}
		 */
		async getGroupRequestsByGroupNumber(groupNumber, opts = {}) {
			if (shouldThrottle('getGroupRequestsByGroupNumber', 100)) {
				posthog.capture('throttled_action', { action: 'getGroupRequestsByGroupNumber' });
				return [];
			}
			const key = Number(groupNumber);
			if (!Number.isFinite(key)) return [];

			if (opts.mode !== 'refresh' && this.loadedByGroup[key]) {
				return this.requestsByGroup[key] ?? [];
			}

			this.loadingByGroup[key] = true;

			try {
				const { data } = await apiClient.get(`/groups/request/group/${key}`);
				const requests = this.extractList(data);

				this.requestsByGroup[key] = requests;
				this.loadedByGroup[key] = true;

				for (const request of requests) {
					const requestNumber = Number(request?.requestNumber ?? request?.request_number);
					if (Number.isFinite(requestNumber)) {
						this.requestByNumber[requestNumber] = request;
						this.loadedByNumber[requestNumber] = true;
					}
				}

				return requests;
			} catch {
				this.requestsByGroup[key] = [];
				this.loadedByGroup[key] = true;
				return [];
			} finally {
				this.loadingByGroup[key] = false;
			}
		},

		/**
		 * Saves a new group request.
		 *
		 * ROUTE: POST /groups/request/save
		 *
		 * @param {Object} payload
		 * @returns {Promise<any|null>}
		 */
		async saveGroupRequest(payload) {
			if (shouldThrottle('saveGroupRequest', 100)) {
				posthog.capture('throttled_action', { action: 'saveGroupRequest' });
				return [];
			}
			if (!payload || typeof payload !== 'object') return null;
			this.saving = true;

			try {
				const { data } = await apiClient.post('/groups/request/save', payload);
				const request = this.extractSingle(data);
				const requestNumber = Number(request?.requestNumber ?? request?.request_number);
				const groupNumber = Number(request?.group?.groupNumber ?? request?.groupNumber ?? request?.group_number ?? payload?.groupNumber ?? payload?.group_number);

				if (Number.isFinite(requestNumber)) {
					this.requestByNumber[requestNumber] = request;
					this.loadedByNumber[requestNumber] = true;
				}

				if (Number.isFinite(groupNumber)) {
					const current = this.requestsByGroup[groupNumber] ?? [];
					const filtered = current.filter((r) => Number(r?.requestNumber ?? r?.request_number) !== requestNumber);
					this.requestsByGroup[groupNumber] = request ? [...filtered, request] : filtered;
					this.loadedByGroup[groupNumber] = true;
				}

				if (this.loadedAll && request) {
					const filtered = (this.allRequests ?? []).filter((r) => Number(r?.requestNumber ?? r?.request_number) !== requestNumber);
					this.allRequests = [...filtered, request];
				}

				return request;
			} catch {
				return null;
			} finally {
				this.saving = false;
			}
		},

		/**
		 * Deletes a group request and removes it from all relevant caches.
		 *
		 * ROUTE: DELETE /groups/request/delete/{request_number}
		 *
		 * @param {number|string} requestNumber
		 * @returns {Promise<boolean>}
		 */
		async deleteGroupRequest(requestNumber) {
			if (shouldThrottle('deleteGroupRequest', 100)) {
				posthog.capture('throttled_action', { action: 'deleteGroupRequest' });
				return [];
			}
			const key = Number(requestNumber);
			if (!Number.isFinite(key)) return false;
			this.deleting = true;

			try {
				await apiClient.delete(`/groups/request/delete/${key}`);

				const existing = this.requestByNumber[key] ?? null;
				const groupNumber = Number(existing?.group?.groupNumber ?? existing?.groupNumber ?? existing?.group_number);

				delete this.requestByNumber[key];
				this.loadedByNumber[key] = true;

				this.allRequests = (this.allRequests ?? []).filter((r) => Number(r?.requestNumber ?? r?.request_number) !== key);

				if (Number.isFinite(groupNumber)) {
					this.requestsByGroup[groupNumber] = (this.requestsByGroup[groupNumber] ?? []).filter((r) => Number(r?.requestNumber ?? r?.request_number) !== key);
				}

				return true;
			} catch {
				return false;
			} finally {
				this.deleting = false;
			}
		},

		/**
		 * Clears all cached group request data.
		 *
		 * @returns {void}
		 */
		reset() {
			this.requestsByGroup = {};
			this.requestByNumber = {};
			this.loadingByGroup = {};
			this.loadingByNumber = {};
			this.saving = false;
			this.deleting = false;
			this.loadedByGroup = {};
			this.loadedByNumber = {};
		}
	}
});
